/**
 * Test Service
 * Handles all test session-related API calls
 */

import apiClient from '../client';
import { API_ENDPOINTS } from '../config';
import {
  TestSession,
  TestSessionsParams,
  PaginatedResponse,
  VersionStats,
} from '../types';

class TestService {
  /**
   * Get all test sessions
   */
  async getTestSessions(params?: TestSessionsParams): Promise<PaginatedResponse<TestSession>> {
    const response = await apiClient.get<PaginatedResponse<TestSession>>(
      API_ENDPOINTS.TESTS.SESSIONS,
      { params }
    );
    return response.data;
  }

  /**
   * Get test session by ID
   */
  async getTestSessionById(id: string): Promise<TestSession> {
    const response = await apiClient.get<TestSession>(
      API_ENDPOINTS.TESTS.BY_ID(id)
    );
    return response.data;
  }

  /**
   * Get test statistics
   */
  async getTestStats(params?: {
    dateFrom?: string;
    dateTo?: string;
    testId?: string;
  }): Promise<{
    totalSessions: number;
    completedSessions: number;
    averageScore: number;
    passRate: number;
  }> {
    const response = await apiClient.get<{
      totalSessions: number;
      completedSessions: number;
      averageScore: number;
      passRate: number;
    }>(API_ENDPOINTS.TESTS.STATS, { params });
    return response.data;
  }

  /**
   * Get opened tests statistics
   */
  async getOpenedTestsStats(): Promise<{
    stats: VersionStats[];
    totals: {
      last24h: number;
      lastWeek: number;
      total: number;
    };
  }> {
    const response = await apiClient.get<{
      stats: VersionStats[];
      totals: {
        last24h: number;
        lastWeek: number;
        total: number;
      };
    }>(API_ENDPOINTS.TESTS.OPENED);
    return response.data;
  }

  /**
   * Get test trends
   */
  async getTestTrends(params?: {
    testId?: string;
    period?: 'day' | 'week' | 'month';
  }): Promise<Array<{
    date: string;
    sessionCount: number;
    passRate: number;
    averageScore: number;
  }>> {
    const response = await apiClient.get<Array<{
      date: string;
      sessionCount: number;
      passRate: number;
      averageScore: number;
    }>>(`${API_ENDPOINTS.TESTS.STATS}/trends`, { params });
    return response.data;
  }

  /**
   * Get test performance by user
   */
  async getTestPerformance(
    userId: string,
    params?: {
      testId?: string;
      dateFrom?: string;
      dateTo?: string;
    }
  ): Promise<Array<{
    testId: string;
    testName: string;
    attemptCount: number;
    bestScore: number;
    averageScore: number;
    passed: boolean;
  }>> {
    const response = await apiClient.get<Array<{
      testId: string;
      testName: string;
      attemptCount: number;
      bestScore: number;
      averageScore: number;
      passed: boolean;
    }>>(`${API_ENDPOINTS.TESTS.SESSIONS}/user/${userId}/performance`, { params });
    return response.data;
  }

  /**
   * Get top performers for a test
   */
  async getTopPerformers(
    testId: string,
    params?: {
      limit?: number;
      dateFrom?: string;
      dateTo?: string;
    }
  ): Promise<Array<{
    userId: string;
    username: string;
    score: number;
    completionTime: number;
    rank: number;
  }>> {
    const response = await apiClient.get<Array<{
      userId: string;
      username: string;
      score: number;
      completionTime: number;
      rank: number;
    }>>(`${API_ENDPOINTS.TESTS.BY_ID(testId)}/top-performers`, { params });
    return response.data;
  }

  /**
   * Get chart data for tests
   */
  async getTestChartData(
    chartType: 'bar' | 'line',
    testId?: string
  ): Promise<Array<{ name: string; [key: string]: string | number }>> {
    const response = await apiClient.get<Array<{ name: string; [key: string]: string | number }>>(
      `${API_ENDPOINTS.TESTS.STATS}/chart`,
      {
        params: {
          chartType,
          testId,
        },
      }
    );
    return response.data;
  }

  /**
   * Create test session
   */
  async createTestSession(data: Partial<TestSession>): Promise<TestSession> {
    const response = await apiClient.post<TestSession>(
      API_ENDPOINTS.TESTS.SESSIONS,
      data
    );
    return response.data;
  }

  /**
   * Update test session
   */
  async updateTestSession(id: string, data: Partial<TestSession>): Promise<TestSession> {
    const response = await apiClient.put<TestSession>(
      API_ENDPOINTS.TESTS.BY_ID(id),
      data
    );
    return response.data;
  }

  /**
   * Submit test answers
   */
  async submitTestAnswers(
    sessionId: string,
    answers: any[]
  ): Promise<{
    score: number;
    passed: boolean;
    correctAnswers: number;
    totalQuestions: number;
  }> {
    const response = await apiClient.post<{
      score: number;
      passed: boolean;
      correctAnswers: number;
      totalQuestions: number;
    }>(`${API_ENDPOINTS.TESTS.BY_ID(sessionId)}/submit`, { answers });
    return response.data;
  }

  /**
   * Export test sessions
   */
  async exportTestSessions(params?: TestSessionsParams): Promise<Blob> {
    const response = await apiClient.getAxiosInstance().get(
      `${API_ENDPOINTS.TESTS.SESSIONS}/export`,
      {
        params,
        responseType: 'blob',
      }
    );
    return response.data;
  }
}

// Export singleton instance
export const testService = new TestService();
export default testService;

